/**
 * useFocusTime Hook
 *
 * Manages focus time settings and block generation via the API.
 */

import { useState, useEffect, useCallback } from 'react';
import { api, FocusTimeSettingsDetail, FocusTimeSettingsInput } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';

export function useFocusTime() {
  const [settings, setSettings] = useState<FocusTimeSettingsDetail | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const result = await api.getFocusTimeSettings();
      setSettings(result.settings);
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to load focus time settings',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updateSettings = useCallback(async (input: FocusTimeSettingsInput) => {
    const updated = await api.updateFocusTimeSettings(input);
    setSettings(updated);
    return updated;
  }, []);

  const generate = useCallback(async () => {
    const result = await api.generateFocusTimeBlocks();
    return result.blocksCreated;
  }, []);

  const regenerate = useCallback(async () => {
    const result = await api.regenerateFocusTimeBlocks();
    return result.blocksCreated;
  }, []);

  const clear = useCallback(async () => {
    const result = await api.clearFocusTimeBlocks();
    return result.deleted;
  }, []);

  return {
    settings,
    loading,
    fetchSettings,
    updateSettings,
    generate,
    regenerate,
    clear,
  };
}
